// Carry Roll ComparisonTable — side-by-side carry/total/roll% (7.4, 10.1).

import { X } from 'lucide-react';
import { useStore } from '../../store';
import { compareScenarios } from '@carry-roll/shared-logic';
import { useAlert } from '@ascend/alert';

export function ComparisonTable() {
  const scenarios = useStore((s) => s.scenarios);
  const removeScenario = useStore((s) => s.removeScenario);
  const distanceUnit = useStore((s) => s.distanceUnit);
  const { confirm } = useAlert();

  const handleRemove = async (id: string, label: string) => {
    const ok = await confirm({
      title: 'Remove scenario?',
      message: `Remove "${label}" from the comparison.`,
      danger: true,
      confirmLabel: 'Remove',
    });
    if (ok) removeScenario(id);
  };

  if (scenarios.length === 0) {
    return (
      <div data-testid="comparison-table" className="panel" style={{ color: 'var(--color-text-muted)' }}>
        Add a scenario to start comparing.
      </div>
    );
  }

  const rows = compareScenarios(
    scenarios.map((s) => ({ id: s.id, label: s.label, request: s.request })),
    distanceUnit,
  );

  return (
    <div className="panel" data-testid="comparison-table">
      <h3 className="subsection-title">
        Comparison
      </h3>
      <div style={{ overflowX: 'auto' }}>
        <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
          <caption className="sr-only">Scenario comparison — carry, total, and offline by setup</caption>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <th scope="col" style={{ textAlign: 'left', padding: 'var(--space-2)' }}>Metric</th>
              {scenarios.map((s) => (
                <th key={s.id} scope="col" style={{ textAlign: 'right', padding: 'var(--space-2)' }}>
                  {s.label}{' '}
                  <button
                    type="button"
                    className="text-xs"
                    aria-label={`Remove ${s.label}`}
                    onClick={() => handleRemove(s.id, s.label)}
                    style={{ color: 'var(--color-danger)' }}
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.metric} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td scope="row" style={{ padding: 'var(--space-2)', color: 'var(--color-text-muted)' }}>{row.metric}</td>
                {row.values.map((v, i) => (
                  <td key={i} style={{ textAlign: 'right', padding: 'var(--space-2)' }}>
                    {v}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
